import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, DesignV2Scope, SegmentedControl } from "../../../design-v2";
import { StackHeader } from "../../../components/product";
import { CURRENT_ANALYSIS } from "../api/insightsApi";
import {
  CYCLE_ROLE,
  WEEK_STATUS,
  buildTimelineColumns,
  findCycle,
  formatDateRange,
} from "../domain/insightsPresentation";
import {
  InsightsSection,
  NoActiveCycleState,
  NoHistoryState,
  SectionError,
  SectionLoading,
  TimeframeSelector,
} from "../components/InsightsChrome";
import { ExecutionSummary, WeeklyAdherenceCharts } from "../components/AdherenceParts";
import { insightsPath, useAdherence, useTimeframeParam } from "./useInsightsData";

// "W1–W4": the cycle weeks the figures cover so far (API week statuses).
function cycleWeekSpan(cycle) {
  const weeks = (cycle?.weeks || []).filter((week) => week.status !== WEEK_STATUS.UPCOMING);
  if (!weeks.length) return null;
  const last = weeks[weeks.length - 1].weekNumber;
  return last === 1 ? "W1" : `W1–W${last}`;
}

function cycleSubtitle(cycle) {
  const name = cycle.name || (cycle.role === CYCLE_ROLE.PREVIOUS ? "Previous cycle" : "Current cycle");
  return cycle.role === CYCLE_ROLE.ACTIVE
    ? `${name} to date (prescription vs. executed)`
    : `${name} (prescription vs. executed)`;
}

function CurrentAnalysisAdherence({ data }) {
  const active = findCycle(data.cycles, CYCLE_ROLE.ACTIVE);
  const previous = findCycle(data.cycles, CYCLE_ROLE.PREVIOUS);
  const [role, setRole] = useState(CYCLE_ROLE.ACTIVE);
  const cycle = (role === CYCLE_ROLE.PREVIOUS ? previous : active) || active;
  const columns = useMemo(() => buildTimelineColumns(data), [data]);
  const highlight = columns.findIndex((column) => column.segment === CYCLE_ROLE.ACTIVE
    && column.status === WEEK_STATUS.IN_PROGRESS);
  const span = cycleWeekSpan(cycle);

  return (
    <>
      <InsightsSection
        title="Execution"
        subtitle={cycleSubtitle(cycle)}
        testId="adherence-execution"
        aside={(
          <span className="lz-insights-card__badges">
            {span ? <Badge tone="action">{span}</Badge> : null}
            {cycle.status === "ARCHIVED" ? <Badge tone="neutral">Archived</Badge> : null}
          </span>
        )}
      >
        {previous ? (
          <SegmentedControl
            label="Cycle"
            className="lz-insights-cycle-switch"
            value={cycle.role}
            onChange={setRole}
            options={[
              { value: CYCLE_ROLE.PREVIOUS, label: "Previous cycle" },
              { value: CYCLE_ROLE.ACTIVE, label: "Current cycle" },
            ]}
          />
        ) : null}
        <ExecutionSummary totals={cycle.totals} headline={cycle.headline} />
      </InsightsSection>

      <InsightsSection title="Weekly progression" subtitle="Cycle weeks, prescribed work in its target week" testId="adherence-weekly">
        <WeeklyAdherenceCharts columns={columns} workoutsAs="dots" highlightIndex={highlight} />
      </InsightsSection>
    </>
  );
}

function HistoricalAdherence({ data }) {
  const columns = useMemo(() => buildTimelineColumns(data), [data]);
  const range = formatDateRange(data.range?.startDate, data.range?.endDate);
  return (
    <>
      <InsightsSection
        title="Execution"
        subtitle={range || null}
        testId="adherence-execution"
      >
        <ExecutionSummary totals={data.totals} headline={data.headline} />
      </InsightsSection>
      <InsightsSection title="Weekly progression" testId="adherence-weekly">
        <WeeklyAdherenceCharts columns={columns} highlightIndex={columns.length - 1} />
      </InsightsSection>
    </>
  );
}

export default function AdherenceScreen() {
  const navigate = useNavigate();
  const [timeframe, setTimeframe] = useTimeframeParam();
  const state = useAdherence(timeframe);
  const isCurrent = timeframe === CURRENT_ANALYSIS;

  let body;
  if (state.status === "loading") body = <SectionLoading label="Loading adherence" />;
  else if (state.status === "error") body = <SectionError what="adherence" onRetry={state.retry} />;
  else if (state.data?.available === false) body = <NoActiveCycleState onBrowseHistory={() => setTimeframe("3M")} />;
  else if (!isCurrent && !(state.data?.weeks || []).length) body = <NoHistoryState timeframe={timeframe} />;
  else if (isCurrent) body = <CurrentAnalysisAdherence key="current" data={state.data} />;
  else body = <HistoricalAdherence data={state.data} />;

  return (
    <DesignV2Scope className="lz-insights lz-insights--detail" data-testid="insights-adherence">
      <StackHeader
        title="Adherence"
        backLabel="Back to Insights"
        onBack={() => navigate(insightsPath("/progress", timeframe))}
      />
      <main className="lz-insights__content">
        <TimeframeSelector value={timeframe} onChange={setTimeframe} />
        {body}
      </main>
    </DesignV2Scope>
  );
}
